import classNames from 'classnames';
import {useEffect, useState, useMemo, useContext, useCallback} from 'react';
import Iframe from 'react-iframe';
import Ticker from 'react-ticker';

import {videoTokenApi} from 'api';
import {TextChat, SessionTextChatProps, SessionWebRTCRoom, Subtitles} from 'components';
import {LOGO_ICON_PATH} from 'constants/images';
import {ClientContext} from 'context';
import {useAudioStreamRecord, useClientSession} from 'hooks';
import {ScreenSharingActivityStatus} from 'interfaces';

import {ClientAsyncRequest} from '../ClientAsyncRequest';
import {InSessionClientControls} from '../InSessionControls';

import './styles.scss';

type Props = {
    TextChatComponent?: React.ComponentType<SessionTextChatProps>;
};

export const InSessionView: React.FC<Props> = ({TextChatComponent}: Props) => {
    const {
        session, audioVideoActivity, webRTCActivities, audioActivity, textChatActivity, screenSharingActivity, coBrowsingActivity,
        webRTCRoomName, videoMinimized, canSwitchCamera, participantId, agent, screenSharingActivityStatus, isScreenSharingPresenter,
        fullScreenVideoDefault, controlsAutoHideEnabled, controlsAutoHideTimeout, controlsInitialVisible, isPictureInPictureEnabled, ticker,
        videoPinEnabled, audioStreamEnabled, isInSession, subtitlesEnabled, transcriptionLanguage, participantIds, audioVideoView,
        audioVideoViewConference, audioVideoAsColumn, asyncRequestToHandle
    } = useClientSession();
    const {
        conversationOnly, fullScreenVideo, onToggleFullScreenVideo, audioEnabled, onToggleAudio, videoEnabled, onToggleVideo,
        endLocalSession, audioDevices, videoDevices, videoConstraints, onTogglePictureInPicture,
        setMediaDevices, notifyWebRTCStartSuccess, notifyWebRTCStartFailed, onLocalNetworkQualityLevelChange,
        onRemoteNetworkQualityLevelChange, setVideoConstraints, participantsNetworkQuality, videoPinParticipant,
        onSubmitMessage, onFileDownload, onChangeTextChatActivityState, setScreenSharingActivityStatus,
        handleVideoPinParticipantUpdate, socket, setTracks, tracks, pinnedParticipantId
    } = useContext(ClientContext);
    const [isControlsActive, setIsControlsActive] = useState(controlsInitialVisible);

    const hasMediaActivities = useMemo(() => Boolean(audioActivity || audioVideoActivity), [audioActivity, audioVideoActivity]);
    const showBackground = useMemo(() =>
        !audioVideoActivity && (!screenSharingActivity || isScreenSharingPresenter) && !coBrowsingActivity && !asyncRequestToHandle,
    [audioVideoActivity, screenSharingActivity, coBrowsingActivity, isScreenSharingPresenter, asyncRequestToHandle]);

    const mediaConstraints = useMemo(() => ({
        audioinput: Boolean(audioActivity || audioVideoActivity),
        ...(Boolean(audioVideoActivity) && {videoinput: videoConstraints})
    }), [audioActivity, audioVideoActivity, videoConstraints]);

    const hasClickableFullscreenActivity = useMemo(() => !!coBrowsingActivity, [coBrowsingActivity]);
    const hasWebRTCActivities = useMemo(() => webRTCActivities.length > 0, [webRTCActivities.length]);

    const shouldRecordAudioStream = useMemo(() =>
        audioStreamEnabled && isInSession && socket?.connected && (!!audioVideoActivity || !!audioActivity),
    [audioStreamEnabled, audioActivity, audioVideoActivity, isInSession, socket?.connected]);

    useAudioStreamRecord({
        shouldRecord: shouldRecordAudioStream,
        socket,
        sessionId: session.id,
        participantId,
        activityId: audioActivity?.id || audioVideoActivity?.id,
        tracks,
        transcriptionLanguage
    });

    const handleMouseOver = useCallback(() => controlsAutoHideEnabled && setIsControlsActive(true), [controlsAutoHideEnabled]);
    const handleMouseOut = useCallback(() => controlsAutoHideEnabled && setIsControlsActive(false), [controlsAutoHideEnabled]);
    const handleTouch = useCallback(() => controlsAutoHideEnabled && setIsControlsActive((prev) => !prev), [controlsAutoHideEnabled]);

    const localNetworkQualityLevel = useMemo(() => participantsNetworkQuality?.[participantId],
        [participantsNetworkQuality, participantId]);
    const remoteNetworkQualityLevel = useMemo(() => participantsNetworkQuality?.[agent?.id],
        [participantsNetworkQuality, agent]);

    const onNewUnreadMessage = useCallback(() => {
        if (!isControlsActive) {
            setIsControlsActive(true);
        }
    }, [isControlsActive]);

    useEffect((() => {
        if (controlsAutoHideEnabled) {
            const resetInitialActiveTimer = setTimeout(() => {
                setIsControlsActive(false);
            }, controlsAutoHideTimeout);

            return () => {
                clearTimeout(resetInitialActiveTimer);
            };
        }
    }));

    return (
        <InSessionClientControls
            isActive={isControlsActive}
            videoPinParticipant={videoPinParticipant}
            hasAudioVideoActivity={!!audioVideoActivity}
            facingMode={(videoConstraints?.facingMode as ConstrainDOMStringParameters)?.exact as string}
            localNetworkQualityLevel={localNetworkQualityLevel}
            remoteNetworkQualityLevel={remoteNetworkQualityLevel}
            isFullScreen={fullScreenVideo}
            isAudioEnabled={audioEnabled}
            videoPinEnabled={videoPinEnabled}
            isVideoEnabled={videoEnabled}
            videoDevices={videoDevices}
            selectedVideoDeviceId={videoConstraints?.deviceId as string}
            pinnedParticipantId={pinnedParticipantId}
            audioVideoAsColumn={audioVideoAsColumn}
            toggleFullScreen={onToggleFullScreenVideo}
            toggleAudio={onToggleAudio}
            toggleVideo={onToggleVideo}
            updateVideoDevice={setVideoConstraints}
            onVideoPinParticipantUpdate={handleVideoPinParticipantUpdate}
            onLeaveSession={endLocalSession}
            onTogglePictureInPicture={onTogglePictureInPicture}
        >
            {({
                renderOverLocalVideoContent,
                renderOverRemoteVideoContent,
                renderOverParticipantContent,
                renderToggleFullScreen,
                renderToggleAudio,
                renderToggleVideo,
                renderLeaveSession,
                renderChangeDevice,
                renderPictureInPicture
            }) => (
                <div
                    className={classNames('vs-in-session-view', {ticker: !!ticker, 'controls-active': isControlsActive})}
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                >
                    {!webRTCRoomName || !hasWebRTCActivities && <div className='blackout' onTouchStart={handleTouch} />}
                    <div className='room-container' onTouchStart={handleTouch}>
                        <SessionWebRTCRoom
                            participantId={participantId}
                            participantIds={participantIds}
                            videoPinParticipant={videoPinParticipant}
                            roomName={webRTCRoomName}
                            getTwilioTokenFn={videoTokenApi.getClientTwilioVideoToken}
                            getKurentoTokenFn={videoTokenApi.getClientInternalWebRTCToken}
                            mediaConstraints={mediaConstraints}
                            videoDevices={videoDevices}
                            audioDevices={audioDevices}
                            audioEnabled={audioEnabled}
                            videoEnabled={videoEnabled}
                            videoMinimized={videoMinimized}
                            fullScreenVideoDefault={fullScreenVideoDefault}
                            fullScreenVideo={fullScreenVideo}
                            isScreenSharingPresenter={isScreenSharingPresenter}
                            audioActivity={audioActivity}
                            audioVideoActivity={audioVideoActivity}
                            screenSharingActivity={screenSharingActivity}
                            screenSharingMinimized={!!asyncRequestToHandle}
                            screenSharingActivityStatus={screenSharingActivityStatus}
                            screenSharingConfirmRequired
                            defaultView={audioVideoView}
                            pinnedParticipantId={pinnedParticipantId}
                            audioVideoAsColumn={audioVideoAsColumn}
                            overLocalVideoContent={renderOverLocalVideoContent()}
                            overRemoteVideoContent={renderOverRemoteVideoContent()}
                            renderOverParticipantContent={renderOverParticipantContent}
                            screenSharingMessageClassName='tw-absolute tw-top-1/2'
                            notifyWebRTCStartSuccess={notifyWebRTCStartSuccess}
                            notifyWebRTCStartFailed={notifyWebRTCStartFailed}
                            onStartScreenSharing={() => setScreenSharingActivityStatus(ScreenSharingActivityStatus.Accepted)}
                            onStopScreenSharing={() => setScreenSharingActivityStatus(ScreenSharingActivityStatus.Rejected)}
                            onLocalNetworkQualityLevelChange={onLocalNetworkQualityLevelChange}
                            onRemoteNetworkQualityLevelChange={onRemoteNetworkQualityLevelChange}
                            onToggleFullScreenVideo={onToggleFullScreenVideo}
                            onEnumerateMediaDevices={setMediaDevices}
                            onEnumerateTracks={setTracks}
                        />
                    </div>
                    {asyncRequestToHandle &&
                        <div className='async-request-container'>
                            <ClientAsyncRequest />
                        </div>}
                    {/* TODO: Move to a separate component */}
                    {coBrowsingActivity && !asyncRequestToHandle &&
                        <Iframe
                            className='co-browsing-iframe'
                            url={coBrowsingActivity.data.link}
                            allow='autoplay'
                            position='fixed'
                            height='100%'
                            width={audioVideoAsColumn ? '75%' : '100%'}
                            styles={{minHeight: '100vh'}}
                        />}

                    {subtitlesEnabled &&
                        <div className='client-subtitles'>
                            <Subtitles
                                session={session}
                                socket={socket}
                                activity={audioVideoActivity || audioActivity}
                            />
                        </div>}

                    {audioVideoActivity && !videoMinimized &&
                        <div
                            className={classNames('top-actions')}
                        >
                            <div className={classNames('in-session-actions', {
                                active: isControlsActive || hasClickableFullscreenActivity,
                                centered: !audioVideoActivity
                            })}
                            >
                                {isPictureInPictureEnabled && renderPictureInPicture()}
                                {!audioVideoViewConference && renderToggleFullScreen()}
                            </div>
                        </div>}
                    {showBackground &&
                        <div className='tw-flex-center tw-flex-col tw-absolute tw-inset-0 tw-p-8'>
                            <img src={LOGO_ICON_PATH} alt='Logo' className='tw-max-h-full tw-opacity-20' />
                        </div>}
                    <div
                        className={classNames('bottom-actions', {
                            share: videoMinimized
                        })}
                    >
                        <div className={classNames('in-session-actions', {
                            active: isControlsActive || hasClickableFullscreenActivity,
                            centered: !hasMediaActivities
                        })}
                        >
                            {/* TODO Commented until functionality will be implemented  */}
                            {/* eslint-disable-next-line react/jsx-curly-newline */}
                            {/* <div className='left'> */}
                            {/* {renderShareScreen()} */}
                            {/* </div> */}
                            <div className={classNames('center', {
                                positioned: !hasMediaActivities
                            })}
                            >
                                {hasMediaActivities && renderToggleAudio()}
                                {!conversationOnly && renderLeaveSession()}
                                {hasMediaActivities && renderToggleVideo({isHidden: !audioVideoActivity})}
                            </div>
                            <div className={classNames('right', {
                                auto: !hasMediaActivities
                            })}
                            >
                                {canSwitchCamera && renderChangeDevice()}
                                {textChatActivity &&
                                    <TextChat
                                        TextChatComponent={TextChatComponent}
                                        expandable={!!textChatActivity}
                                        session={session}
                                        onSubmitMessage={onSubmitMessage}
                                        onFileDownload={onFileDownload}
                                        onChangeTextChatActivityState={onChangeTextChatActivityState}
                                        onNewUnreadMessage={onNewUnreadMessage}
                                    />}
                            </div>
                        </div>
                    </div>
                    {ticker &&
                        <div className='tw-fixed tw-bottom-0 tw-w-full tw-h-5 tw-flex tw-items-center' style={ticker.containerStyle}>
                            <div className='tw-w-full'>
                                <Ticker direction={ticker.direction} speed={ticker.speed}>
                                    {() => <div dangerouslySetInnerHTML={{__html: ticker.content}} />}
                                </Ticker>
                            </div>
                        </div>}
                </div>
            )}
        </InSessionClientControls>
    );
};
